import { Stroke, Style } from 'ol/style';
import Feature from 'ol/Feature';
import MultiPolygon from 'ol/geom/MultiPolygon';

import type { Extent } from 'ol/extent';
import type Geometry from 'ol/geom/Geometry';

import MapConsts from '@/components/map/consts/MapConsts';
import { FEATURE_TYPE } from '@/components/map/consts/mapFeatureConsts';
import type { QualityColorMode } from '@/components/map/consts/MapConsts';
import MapHelpers from '@/components/map/utils/MapHelpers';
import MapStyles from '@/components/map/features/MapStyles';
import { getDrawableQualityPolygons } from '@/components/map/features/mapQualityGeometry';

import type {
	BaseStationFeatureItem,
	BaseStationFeatureItemWithSubnetworkColors,
	BaselineFeatureData,
	BaselineWithCoords,
	ConnectedBaseStation,
	CcmGridpointFeatureData,
	CcmOtherGridpointFeatureData,
	CoverageContourFeature,
	FeatureData,
	GridpointDisplay,
	LonLatRadius,
	MapMode,
	ProfileFeatureItem,
	QualityRectangleFeature,
	QualityRectangleProperties,
	RoverHistoryCurrentFeatureData,
	RoverHistoryCurrentDisplay,
	RoverHistoryFragmentFeatureData,
	RoverHistoryFragmentDisplay,
	VRSParticipantRoverDisplay,
} from '@/components/map/types/mapFeatureTypes';


type MapFeature = Feature<Geometry>;


export type { MapFeature };

const hasConnectedBaseStation = (
	item: VRSParticipantRoverDisplay,
): item is VRSParticipantRoverDisplay & { connectedBaseStation: ConnectedBaseStation } => (
	item.connectedBaseStation != null
);


const createLinksFeatures = (coords: VRSParticipantRoverDisplay[]): MapFeature[] => {
	return coords.filter(hasConnectedBaseStation).map(item => {
		const feature = new Feature({
			type: FEATURE_TYPE.getterLink,
			geometry: MapHelpers.getLine(item, item.connectedBaseStation),
		});
		const colors = MapHelpers.getServiceColors(item.connectedBaseStation.service, true, true);
		feature.setStyle(MapStyles.getLinkStyle(colors));
		return feature;
	});
};


const createRoverHistoryCurrentFeatures = (coords: RoverHistoryCurrentDisplay[]): MapFeature[] => {
	return coords.map(item => {
		const featureData: RoverHistoryCurrentFeatureData = item;
		const iconFeature = new Feature({
			type: FEATURE_TYPE.roverHistoryCurrent,
			featureData: featureData,
			geometry: MapHelpers.getPoint(item),
		});
		iconFeature.setStyle(MapStyles.getRoverHistoryRoverStyle(featureData.quality, true, false));
		return iconFeature;
	});
};


const createRoverHistoryFragmentsFeatures = (
	coords: RoverHistoryFragmentDisplay[],
	roversHistoryAutoUpdate: boolean,
	viewportExtent: Extent | null,
): MapFeature[] => {
	// TODO: review roversHistoryStore
	const filtered = coords.filter(MapHelpers.filterInsideViewport(viewportExtent));
	return filtered.flatMap((item, index) => {
		const isLast = index === filtered.length - 1;
		const featureData: RoverHistoryFragmentFeatureData = item;
		const result: MapFeature[] = [];
		const fragmentFeature = new Feature({
			type: FEATURE_TYPE.roverHistoryFragment,
			featureData: featureData,
			geometry: MapHelpers.getLine({ lon: item.lon1, lat: item.lat1 }, { lon: item.lon2, lat: item.lat2 }),
		});
		fragmentFeature.setStyle(MapStyles.getRoverHistoryFragmentLineStyle(featureData.quality, false));
		result.push(fragmentFeature);
		if (item.is_break) {
			const breakFeature = new Feature({
				type: FEATURE_TYPE.roverHistoryFragmentBreakEnd,
				featureData: featureData,
				geometry: MapHelpers.getPoint({ lon: item.lon2, lat: item.lat2 }),
			});
			breakFeature.setStyle(MapStyles.getRoverHistoryFragmentDotStyle(featureData.quality, true, false));
			result.push(breakFeature);
		} else if (isLast && !roversHistoryAutoUpdate) {
			const iconFeature = new Feature({
				type: FEATURE_TYPE.roverHistoryFragmentLastEnd,
				featureData: featureData,
				geometry: MapHelpers.getPoint({ lon: item.lon2, lat: item.lat2 }),
			});
			iconFeature.setStyle(MapStyles.getRoverHistoryRoverStyle(featureData.quality, false, false));
			result.push(iconFeature);
		} else {
			const normalEndFeature = new Feature({
				type: FEATURE_TYPE.roverHistoryFragmentNormalEnd,
				featureData: featureData,
				geometry: MapHelpers.getPoint({ lon: item.lon2, lat: item.lat2 }),
			});
			normalEndFeature.setStyle(MapStyles.getRoverHistoryFragmentDotStyle(featureData.quality, false, false));
			result.push(normalEndFeature);
		}
		return result;
	});
};


const createBaselineLinksFeatures = (coords: BaselineWithCoords[]): MapFeature[] => {
	const result: MapFeature[] = [];
	for (const item of coords) {
		const featureData: BaselineFeatureData = item;
		if (item.base.id === item.rover.id) {
			const selfCircleFeature = new Feature({
				type: FEATURE_TYPE.baselineLinkSelfCircle,
				featureData: featureData,
				geometry: MapHelpers.getPoint(featureData.base),
			});
			selfCircleFeature.setStyle(MapStyles.getBaselineSelfCircleStyle(featureData));
			result.push(selfCircleFeature);
		} else {
			const linkFeature = new Feature({
				type: FEATURE_TYPE.baselineLinkLine,
				featureData: featureData,
				geometry: MapHelpers.getLine(featureData.base, featureData.rover),
			});
			linkFeature.setStyle(MapStyles.getBaselineLinkStyle(featureData, false));
			result.push(linkFeature);
			const arrowFeature = new Feature({
				type: FEATURE_TYPE.baselineLinkArrowHead,
				featureData: featureData,
				geometry: MapHelpers.getPoint(featureData.rover),
			});
			arrowFeature.setStyle(MapStyles.getArrowHeadStyle(
				featureData.base,
				featureData.rover,
				MapHelpers.getBaselineColor(featureData),
			));
			result.push(arrowFeature);
		}
	}
	return result;
};


const createActiveGettersFeatures = (coords: VRSParticipantRoverDisplay[]): MapFeature[] => {
	return coords.map(item => {
		const iconFeature = new Feature({
			type: FEATURE_TYPE.getterActive,
			featureData: item.featureData,
			geometry: MapHelpers.getPoint(item),
		});
		iconFeature.setStyle(MapStyles.getActiveGetterStyle(item.featureData.service, item.featureData.connectedBaseStation, false));
		return iconFeature;
	});
};


const createFailedGettersFeatures = (coords: VRSParticipantRoverDisplay[]): MapFeature[] => {
	return coords.map(item => {
		const iconFeature = new Feature({
			type: FEATURE_TYPE.getterFailed,
			featureData: item.featureData,
			geometry: MapHelpers.getPoint(item),
		});
		iconFeature.setStyle(MapStyles.getFailedGetterStyle(item.featureData.service, item.featureData.is_new, false));
		return iconFeature;
	});
};


const createGeoFencingCircleFeature = (item: LonLatRadius): MapFeature[] => {
	const circleFeature = new Feature({
		type: FEATURE_TYPE.geoFencingCircle,
		geometry: MapHelpers.getCircle(item),
	});
	const style = [
		new Style({
			zIndex: MapConsts.Z_INDEX.geoFencingCircleOuter,
			stroke: new Stroke({
				color: 'white',
				width: 8,
			}),
		}),
		new Style({
			zIndex: MapConsts.Z_INDEX.geoFencingCircleMiddle,
			stroke: new Stroke({
				color: 'black',
				width: 4,
			}),
		}),
		new Style({
			zIndex: MapConsts.Z_INDEX.geoFencingCircleInner,
			stroke: new Stroke({
				color: 'white',
				width: 2,
			}),
		}),
	];
	circleFeature.setStyle(style);
	return [circleFeature];
};


const createBaseStationsFeatures = (
	coords: BaseStationFeatureItem[],
	selectedBaseStationId: number | null,
	mapMode: MapMode,
	focusedMountpoint: string,
): MapFeature[] => {
	return coords.map(item => {
		const iconFeature = new Feature({
			type: FEATURE_TYPE.baseStation,
			featureData: item.featureData,
			geometry: MapHelpers.getPoint(item),
		});
		const style = MapStyles.getBaseStationStyleMain(
			mapMode,
			FEATURE_TYPE.baseStation,
			item.featureData,
			false,
			item.featureData.id === selectedBaseStationId,
			MapHelpers.checkIsFocusedMountpoint(item.featureData, focusedMountpoint),
		);
		iconFeature.setStyle(style);
		return iconFeature;
	});
};


const createBaseStationsCircleFeatures = (coords: BaseStationFeatureItem[], fillCoverageArea: boolean): MapFeature[] => {
	// у включенных БС рисуем области действия
	return coords
		.filter(item => !['stopped', 'deleted'].includes(item.featureData.status))
		.map(item => {
			const circleFeature = new Feature({
				type: FEATURE_TYPE.baseStationCircle,
				geometry: MapHelpers.getCircle(item),
			});
			circleFeature.setStyle(MapStyles.getCircleStyle(item.featureData.statusOnlineData.badge, false, fillCoverageArea));
			return circleFeature;
		});
};


const createBaseStationsLabelFeatures = (
	coords: BaseStationFeatureItem[],
	focusedMountpoint: string,
	mapMode: MapMode,
	fillCoverageArea: boolean,
): MapFeature[] => {
	const labelOpacity = MapStyles.getBaseStationLabelOpacity(mapMode, fillCoverageArea);
	// у БС добавляем подписи
	return coords.map(item => {
		const labelFeature = new Feature({
			type: FEATURE_TYPE.baseStationLabel,
			featureData: item.featureData,
			geometry: MapHelpers.getPoint(item),
		});
		labelFeature.setStyle(MapStyles.getLabelStyle(
			item.featureData.mountpointDisplay,
			MapHelpers.checkIsFocusedMountpoint(item.featureData, focusedMountpoint),
			labelOpacity,
		));
		return labelFeature;
	});
};


const createBaseStationsSubnetworkCirclesFeatures = (
	coords: BaseStationFeatureItemWithSubnetworkColors[],
): MapFeature[] => {
	return coords.map(item => {
		const iconFeature = new Feature({
			type: FEATURE_TYPE.baseStationSubnetworkCircles,
			featureData: item.featureData,
			geometry: MapHelpers.getPoint(item),
		});
		const style = MapStyles.getBaseStationSubnetworkCirclesStyle(item.featureData.colors);
		iconFeature.setStyle(style);
		return iconFeature;
	});
};


const createVirtualBSFeatures = (coords: VRSParticipantRoverDisplay[]): MapFeature[] => {
	// берем только геттеры с подключенной БС
	// из них берем на отрисовку только VRS. Реальные БС уже нарисованы
	return MapHelpers.filterVRSFeaturesRemoveDuplicateStations(coords).filter(hasConnectedBaseStation).map(item => {
		const iconFeature = new Feature({
			type: FEATURE_TYPE.vrsStation,
			geometry: MapHelpers.getPoint(item.connectedBaseStation),
		});
		iconFeature.setStyle(MapStyles.getBaseStationStyle('default', false, false, true, true, true));
		return iconFeature;
	});
};


const createVirtualBSCircleFeatures = (coords: VRSParticipantRoverDisplay[], fillCoverageArea: boolean): MapFeature[] => {
	// у VRS рисуем области действия
	return MapHelpers.filterVRSFeaturesRemoveDuplicateStations(coords).filter(hasConnectedBaseStation).map(item => {
		const circleFeature = new Feature({
			type: FEATURE_TYPE.vrsStationCircle,
			geometry: MapHelpers.getCircle(item.connectedBaseStation),
		});
		circleFeature.setStyle(MapStyles.getCircleStyle('default', true, fillCoverageArea));
		return circleFeature;
	});
};


const createGridpointsFeatures = (coords: GridpointDisplay[]): MapFeature[] => {
	return coords.map(item => {
		const iconFeature = new Feature({
			type: FEATURE_TYPE.gridpoint,
			featureData: item.featureData,
			geometry: MapHelpers.getPoint(item),
		});
		iconFeature.setStyle(MapStyles.getGridpointStyle(item.featureData.label));
		return iconFeature;
	});
};


const createProfilesFeatures = (coords: ProfileFeatureItem[]): MapFeature[] => {
	return coords.flatMap(item => {
		const iconFeature = new Feature({
			type: FEATURE_TYPE.profileGeoFencingCenter,
			geometry: MapHelpers.getPoint(item),
		});
		iconFeature.setStyle(MapStyles.getGridpointStyle(item.username));
		const circleFeature = new Feature({
			type: FEATURE_TYPE.profileGeoFencingCircle,
			geometry: MapHelpers.getCircle(item),
		});
		const style = [
			new Style({
				zIndex: MapConsts.Z_INDEX.geoFencingCircleOuter,
				stroke: new Stroke({
					color: 'white',
					width: 3,
				}),
			}),
			new Style({
				zIndex: MapConsts.Z_INDEX.geoFencingCircleInner,
				stroke: new Stroke({
					color: 'black',
					width: 1,
				}),
			}),
		];
		circleFeature.setStyle(style);
		return [iconFeature, circleFeature];
	});
};


const createCoverageContourFeatures = (payload: CoverageContourFeature[][]): MapFeature[] => {
	return payload.flatMap(items => items.flatMap(item => {
		if (!Array.isArray(item.coordinates)) {
			return [];
		}
		const geometry = new MultiPolygon(item.coordinates.map(polygon => polygon.map(MapHelpers.transformFromLonLatArray)));
		const contourFeature = new Feature({
			type: FEATURE_TYPE.coverageContour,
			featureData: {
				...(item.properties ?? {}),
			} satisfies FeatureData,
			geometry: geometry,
		});
		contourFeature.setStyle(MapStyles.getCoverageContourStyle());
		return [contourFeature];
	}));
};


const clampQualityColorValue = (value: number): number => Math.min(Math.max(value, 0), 1);


const computeMaxQEff = (payload: QualityRectangleFeature[][]): number => {
	let maxQEff = 0;
	for (const items of payload) {
		for (const item of items) {
			if (getDrawableQualityPolygons(item).length === 0) {
				continue;
			}
			const qEff = Number(item.properties?.q_eff);
			if (!Number.isNaN(qEff) && qEff > maxQEff) {
				maxQEff = qEff;
			}
		}
	}
	return Math.max(MapConsts.QUALITY_COLOR_Q_EFF_MIN_MAX, maxQEff);
};


const computeQualityColorValue = (
	props: QualityRectangleProperties,
	colorMode: QualityColorMode,
	maxQEff: number,
): number => {
	if (colorMode === MapConsts.QUALITY_COLOR_MODE.qEff) {
		const qEff = Number(props.q_eff);
		if (Number.isNaN(qEff)) {
			return 0;
		}
		return clampQualityColorValue(qEff / maxQEff);
	}
	return clampQualityColorValue(Number(props.vrs_pst));
};


const createQualityRectangleFeatures = (
	payload: QualityRectangleFeature[][],
	colorMode: QualityColorMode,
): MapFeature[] => {
	const maxQEff = colorMode === MapConsts.QUALITY_COLOR_MODE.qEff
		? computeMaxQEff(payload)
		: 1;
	return payload.flatMap(items => items.flatMap(item => {
		const props = item.properties;
		const polygons = getDrawableQualityPolygons(item);
		if (props == null || polygons.length === 0) {
			return [];
		}
		const qColorValue = computeQualityColorValue(props, colorMode, maxQEff);
		const featureData = {
			...props,
			q_color_value: qColorValue,
		} satisfies FeatureData;
		const qualityRectangleFeature = new Feature({
			type: FEATURE_TYPE.qualityRectangle,
			featureData: featureData,
			geometry: new MultiPolygon(
				polygons.map(polygon => polygon.rings.map(MapHelpers.transformFromLonLatArray)),
			),
		});
		qualityRectangleFeature.setStyle(MapStyles.getQualityRectangleStyle(qColorValue, false));
		return [qualityRectangleFeature];
	}));
};


const createAnchorPointFeature = (lat: number, lon: number): MapFeature[] => {
	const iconFeature = new Feature({
		type: FEATURE_TYPE.anchorPoint,
		featureData: {
			lat: lat,
			lon: lon,
		} satisfies FeatureData,
		geometry: MapHelpers.getPoint({ lat, lon }),
	});
	iconFeature.setStyle(MapStyles.getAnchorPointStyle());
	return [iconFeature];
};


const createCnetConstructorGridpointFeature = (
	lat: number,
	lon: number,
	label: string,
	selected: boolean,
	gpIndex: number,
	hovered: boolean,
): MapFeature[] => {
	const featureData = {
		label: label,
		selected: selected,
		gpIndex: gpIndex,
	} satisfies CcmGridpointFeatureData;
	const iconFeature = new Feature({
		type: FEATURE_TYPE.cnetConstructorGridpoint,
		featureData: featureData,
		geometry: MapHelpers.getPoint({ lat, lon }),
	});
	iconFeature.setStyle(MapStyles.getCnetConstructorGridpointStyle(featureData.selected, hovered, featureData.gpIndex));
	return [iconFeature];
};


const createCnetConstructorOtherGridpointFeature = (
	lat: number,
	lon: number,
	label: string,
	color: string,
): MapFeature[] => {
	const featureData = {
		label: label,
		color: color,
	} satisfies CcmOtherGridpointFeatureData;
	const iconFeature = new Feature({
		type: FEATURE_TYPE.cnetConstructorOtherGridpoint,
		featureData: featureData,
		geometry: MapHelpers.getPoint({ lat, lon }),
	});
	iconFeature.setStyle(MapStyles.getCnetConstructorOtherGridpointStyle(featureData.label, featureData.color));
	return [iconFeature];
};


export default {
	createLinksFeatures,
	createBaselineLinksFeatures,
	createRoverHistoryCurrentFeatures,
	createRoverHistoryFragmentsFeatures,
	createActiveGettersFeatures,
	createFailedGettersFeatures,
	createGeoFencingCircleFeature,
	createBaseStationsFeatures,
	createBaseStationsCircleFeatures,
	createBaseStationsLabelFeatures,
	createBaseStationsSubnetworkCirclesFeatures,
	createVirtualBSFeatures,
	createVirtualBSCircleFeatures,
	createGridpointsFeatures,
	createProfilesFeatures,
	createCoverageContourFeatures,
	createQualityRectangleFeatures,
	createAnchorPointFeature,
	createCnetConstructorGridpointFeature,
	createCnetConstructorOtherGridpointFeature,
};
